import { useEffect, useState } from "react";
import { listJobs, deleteJob, downloadJobPdf, toggleShareJob, promoteToTemplate, createShare, formatApiError } from "@/lib/api";
import { JobPhotosDialog } from "@/components/JobPhotos";
import { ShareDialog } from "@/components/ShareButton";
import { Section } from "@/components/CalcUI";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Trash2, Lock, FileDown, Share2, Check, Bookmark, BookmarkPlus, Camera, Link2 } from "lucide-react";
import { toast } from "sonner";

export default function SavedJobs() {
  const { isAuthed, hasPro, user, setShowAuth } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [photosJob, setPhotosJob] = useState(null);
  const [publicShare, setPublicShare] = useState(null);

  const load = async () => {
    if (!isAuthed) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setJobs(await listJobs());
    } catch (e) {
      console.error("Failed to load saved jobs", e);
      toast.error("Could not load saved jobs");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed]);

  const remove = async (id) => {
    await deleteJob(id);
    toast.success("Deleted");
    load();
  };

  const share = async (id, current) => {
    try {
      await toggleShareJob(id, !current);
      toast.success(!current ? "Shared to team" : "Un-shared");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const promote = async (j) => {
    if (!j.is_template) {
      const rig = window.prompt("Rig name (optional):", j.rig_name || "");
      const well = window.prompt("Well name (optional):", j.well_name || "");
      await promoteToTemplate(j.id, true, rig || null, well || null);
      toast.success("Saved as template");
    } else {
      await promoteToTemplate(j.id, false);
      toast.success("Removed from templates");
    }
    load();
  };

  const shareLink = async (j) => {
    try {
      setPublicShare(await createShare({
        title: j.name, category: j.category, calculator: j.calculator,
        inputs: j.inputs, outputs: j.outputs, units: j.units || "field", job_id: j.id,
      }));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not create share link");
    }
  };

  const setJobPhotos = (id, photos) => setJobs((js) => js.map((x) => (x.id === id ? { ...x, photos } : x)));

  const download = async (j) => {
    try {
      await downloadJobPdf(j.id, `${j.name}.pdf`);
    } catch (e) {
      toast.error("PDF failed");
    }
  };

  if (!isAuthed) {
    return (
      <div data-testid="saved-jobs-page">
        <Section title="Saved Jobs" subtitle="Save your calculations across every session and every rig.">
          <div className="border border-[#334155] bg-[#11151c] p-8 text-center">
            <Lock className="w-8 h-8 text-[#f59e0b] mx-auto mb-3" />
            <p className="mono text-sm text-[#94a3b8] mb-4">Sign in to save and revisit your jobs.</p>
            <button
              data-testid="jobs-signin-btn"
              onClick={() => setShowAuth(true)}
              className="bg-[#f59e0b] hover:bg-[#fbbf24] text-[#0b0e13] px-4 py-2 mono text-xs tracking-widest uppercase font-bold"
            >
              Sign in
            </button>
          </div>
        </Section>
      </div>
    );
  }

  if (!hasPro) {
    return (
      <div data-testid="saved-jobs-page">
        <Section title="Saved Jobs" subtitle="Pro plan required to save jobs.">
          <div className="border border-[#334155] bg-[#11151c] p-8 text-center">
            <Lock className="w-8 h-8 text-[#f59e0b] mx-auto mb-3" />
            <p className="mono text-sm text-[#94a3b8] mb-4">Your trial has ended. Upgrade to save unlimited jobs.</p>
            <button
              data-testid="jobs-upgrade-btn"
              onClick={() => navigate("/pricing")}
              className="bg-[#f59e0b] hover:bg-[#fbbf24] text-[#0b0e13] px-4 py-2 mono text-xs tracking-widest uppercase font-bold"
            >
              See plans →
            </button>
          </div>
        </Section>
      </div>
    );
  }

  const canShare = user?.plan === "team" || user?.role === "admin";

  return (
    <div data-testid="saved-jobs-page">
      <Section title="Saved Jobs" subtitle="Every calculation you've saved, ordered by most recent. Download a signed PDF report, or share with your team.">
        {loading ? (
          <div className="mono text-sm text-[#94a3b8]">Loading…</div>
        ) : jobs.length === 0 ? (
          <div className="border border-[#334155] p-8 text-center mono text-sm text-[#94a3b8]">
            No jobs saved yet. Run a calculation and hit SAVE.
          </div>
        ) : (
          <div className="border border-[#334155]">
            <table className="w-full mono text-sm">
              <thead>
                <tr className="border-b border-[#334155] bg-[#0b0e13] text-xs uppercase tracking-widest text-[#94a3b8]">
                  <th className="text-left px-4 py-2">Name</th>
                  <th className="text-left px-4 py-2">Category</th>
                  <th className="text-left px-4 py-2">Calculator</th>
                  <th className="text-left px-4 py-2">Units</th>
                  <th className="text-left px-4 py-2">Saved</th>
                  <th className="text-right px-4 py-2">Result</th>
                  <th className="px-4 py-2 w-40 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j, i) => (
                  <tr
                    key={j.id}
                    data-testid={`job-row-${j.id}`}
                    className={i % 2 === 0 ? "bg-[#0b0e13]" : "bg-[#11151c]"}
                  >
                    <td className="px-4 py-2 text-[#f8fafc]">
                      {j.name}
                      {j.is_template && (
                        <span className="ml-2 text-[9px] mono tracking-widest text-[#f59e0b] border border-[#f59e0b]/50 px-1">
                          TPL
                        </span>
                      )}
                      {j.shared && (
                        <span className="ml-2 text-[9px] mono tracking-widest text-[#10b981] border border-[#10b981]/50 px-1">
                          SHARED
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-[#94a3b8]">{j.category}</td>
                    <td className="px-4 py-2 text-[#f59e0b] text-xs">{j.calculator}</td>
                    <td className="px-4 py-2 text-[#94a3b8] text-xs">{j.units}</td>
                    <td className="px-4 py-2 text-[#94a3b8] text-xs">{new Date(j.created_at).toLocaleString()}</td>
                    <td className="px-4 py-2 text-right text-[#f8fafc] max-w-xs truncate">
                      {j.calculator === "tally" && j.outputs?.summary ? (
                        <span>
                          <span className="text-[#94a3b8]">bit=</span>{j.outputs.summary.bit_depth_ft} ft · <span className="text-[#94a3b8]">ann=</span>{j.outputs.summary.annular_volume_bbl} bbl
                        </span>
                      ) : Object.entries(j.outputs).slice(0, 1).map(([k, v]) => (
                        <span key={k}>
                          <span className="text-[#94a3b8]">{k}=</span>
                          <span>{typeof v === "object" ? JSON.stringify(v) : v}</span>
                        </span>
                      ))}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          data-testid={`photos-job-${j.id}`}
                          title="Well site photos"
                          onClick={() => setPhotosJob(j)}
                          className="relative text-[#94a3b8] hover:text-[#f59e0b]"
                        >
                          <Camera className="w-4 h-4" />
                          {j.photos?.length > 0 && (
                            <span data-testid={`photos-count-${j.id}`} className="absolute -top-2 -right-2 bg-[#f59e0b] text-[#0b0e13] text-[9px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center">
                              {j.photos.length}
                            </span>
                          )}
                        </button>
                        <button
                          data-testid={`link-job-${j.id}`}
                          title="Public share link"
                          onClick={() => shareLink(j)}
                          className="text-[#94a3b8] hover:text-[#f59e0b]"
                        >
                          <Link2 className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`promote-job-${j.id}`}
                          title={j.is_template ? "Remove template" : "Save as template"}
                          onClick={() => promote(j)}
                          className={j.is_template ? "text-[#f59e0b]" : "text-[#94a3b8] hover:text-[#f59e0b]"}
                        >
                          {j.is_template ? <Bookmark className="w-4 h-4" /> : <BookmarkPlus className="w-4 h-4" />}
                        </button>
                        <button
                          data-testid={`pdf-job-${j.id}`}
                          title="Download PDF"
                          onClick={() => download(j)}
                          className="text-[#94a3b8] hover:text-[#f59e0b]"
                        >
                          <FileDown className="w-4 h-4" />
                        </button>
                        {canShare && (
                          <button
                            data-testid={`share-job-${j.id}`}
                            title={j.shared ? "Un-share" : "Share to team"}
                            onClick={() => share(j.id, j.shared)}
                            className={j.shared ? "text-[#10b981]" : "text-[#94a3b8] hover:text-[#f59e0b]"}
                          >
                            {j.shared ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
                          </button>
                        )}
                        <button
                          data-testid={`delete-job-${j.id}`}
                          onClick={() => remove(j.id)}
                          className="text-[#94a3b8] hover:text-[#ef4444]"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
      {photosJob && (
        <JobPhotosDialog job={photosJob} editable onClose={() => setPhotosJob(null)} onChange={(ph) => setJobPhotos(photosJob.id, ph)} />
      )}
      {publicShare && <ShareDialog share={publicShare} onClose={() => setPublicShare(null)} />}
    </div>
  );
}
